<template>
  <div
    ref="viewer"
    class="furniture-3d-viewer">
    <div class="furniture-3d-viewer__toolbar">
      <a-popover
        v-model:open="isSettingsOpen"
        placement="rightTop"
        overlay-class-name="furniture-3d-viewer-settings-popover"
        trigger="hover">
        <template #content>
          <div class="furniture-3d-viewer__settings-panel">
            <div class="furniture-3d-viewer__settings-section-label">
              Качество
            </div>
            <div class="furniture-3d-viewer__settings-section">
              <a-select
                v-model:value="selectedQuality"
                :disabled="!qualityOptions.length"
                :options="qualityOptions"
                size="small"
                class="furniture-3d-viewer__quality" />
            </div>
          </div>
        </template>

        <a-tooltip
          title="Настройки вида"
          placement="right">
          <a-button
            class="furniture-3d-viewer__tool-button"
            type="text">
            <template #icon>
              <setting-outlined />
            </template>
          </a-button>
        </a-tooltip>
      </a-popover>

      <div class="furniture-3d-viewer__divider" />

      <a-tooltip
        title="Скачать GLB"
        placement="right">
        <a-button
          class="furniture-3d-viewer__tool-button"
          type="text"
          :disabled="!selectedModelUrl"
          @click="downloadModel">
          <template #icon>
            <download-outlined />
          </template>
        </a-button>
      </a-tooltip>

      <div class="furniture-3d-viewer__divider" />

      <a-tooltip
        :title="isFullscreen ? 'Выйти из полноэкранного режима' : 'Во весь экран'"
        placement="right">
        <a-button
          class="furniture-3d-viewer__tool-button"
          type="text"
          @click="toggleFullscreen">
          <template #icon>
            <fullscreen-exit-outlined v-if="isFullscreen" />
            <fullscreen-outlined v-else />
          </template>
        </a-button>
      </a-tooltip>

      <div class="furniture-3d-viewer__divider" />

      <a-tooltip
        title="Приблизить"
        placement="right">
        <a-button
          class="furniture-3d-viewer__tool-button"
          type="text"
          :disabled="!hasModel"
          @click="zoomIn">
          <template #icon>
            <zoom-in-outlined />
          </template>
        </a-button>
      </a-tooltip>

      <a-tooltip
        title="Отдалить"
        placement="right">
        <a-button
          class="furniture-3d-viewer__tool-button"
          type="text"
          :disabled="!hasModel"
          @click="zoomOut">
          <template #icon>
            <zoom-out-outlined />
          </template>
        </a-button>
      </a-tooltip>
    </div>

    <div
      ref="container"
      class="furniture-3d-viewer__canvas" />

    <div
      v-if="isLoading"
      class="furniture-3d-viewer__message">
      <span>Загрузка 3D-модели...</span>
      <div class="furniture-3d-viewer__progress">
        <div
          class="furniture-3d-viewer__progress-value"
          :class="{ 'furniture-3d-viewer__progress-value--indeterminate': loadProgress == null }"
          :style="loadProgress == null ? undefined : { width: `${loadProgress}%` }" />
      </div>
      <span v-if="loadProgress != null">
        {{ loadProgress }}%
      </span>
    </div>

    <div
      v-else-if="errorMessage"
      class="furniture-3d-viewer__message furniture-3d-viewer__message--error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup>
import {
  computed, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import {
  DownloadOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  SettingOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons-vue';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { model3dLoader } from '@/services/model3dLoader';

const props = defineProps({
  active: {
    type: Boolean,
    default: true,
  },
  modelVariants: {
    type: Array,
    default: () => [],
  },
  defaultQuality: {
    type: String,
    default: 'high',
  },
  modelName: {
    type: String,
    default: 'furniture',
  },
});

const model3dQualities = ['high', 'medium', 'low'];
const model3dQualityLabels = {
  high: 'Высокое',
  medium: 'Стандартное',
  low: 'Низкое',
};

const normalizedDefaultQuality = computed(() => {
  const value = String(props.defaultQuality || '').toLowerCase();
  return model3dQualities.includes(value) ? value : 'high';
});
const defaultQualityIndex = computed(() => model3dQualities.indexOf(normalizedDefaultQuality.value));
const qualityFallbackOrder = computed(() => [
  ...model3dQualities.slice(defaultQualityIndex.value),
  ...model3dQualities.slice(0, defaultQualityIndex.value).reverse(),
]);
const variantsByQuality = computed(() => Object.fromEntries(
  (props.modelVariants || []).map((variant) => [variant.quality, variant]),
));
const qualityOptions = computed(() => model3dQualities
  .filter((quality) => variantsByQuality.value[quality]?.glb?.url)
  .map((quality) => ({
    label: model3dQualityLabels[quality],
    value: quality,
  })));
const fallbackVariant = computed(() => qualityFallbackOrder.value
  .map((quality) => variantsByQuality.value[quality])
  .find((variant) => variant?.glb?.url));

const viewer = ref(null);
const container = ref(null);
const isLoading = ref(false);
const errorMessage = ref('');
const loadProgress = ref(null);
const selectedQuality = ref(normalizedDefaultQuality.value);
const isFullscreen = ref(false);
const isSettingsOpen = ref(false);
const hasModel = ref(false);
const selectedModelUrl = computed(() => {
  const selectedVariant = variantsByQuality.value[selectedQuality.value];
  const variantWithGlb = selectedVariant?.glb?.url ? selectedVariant : fallbackVariant.value;

  return variantWithGlb?.glb?.url || '';
});

let animationFrameId = null;
let camera = null;
let controls = null;
let grid = null;
let model = null;
let renderer = null;
let resizeObserver = null;
let scene = null;
let loadRequestId = 0;

const disposeMaterial = (material) => {
  Object.values(material).forEach((value) => {
    if (value?.isTexture) {
      value.dispose();
    }
  });

  material.dispose();
};

const disposeObject = (object) => {
  object.traverse((child) => {
    child.geometry?.dispose();

    if (Array.isArray(child.material)) {
      child.material.forEach(disposeMaterial);
    } else if (child.material) {
      disposeMaterial(child.material);
    }
  });
};

const clearModel = () => {
  if (model) {
    scene.remove(model);
    disposeObject(model);
    model = null;
    hasModel.value = false;
  }

  if (grid) {
    scene.remove(grid);
    grid.geometry.dispose();
    grid.material.dispose();
    grid = null;
  }
};

const render = () => {
  if (renderer && scene && camera) {
    renderer.render(scene, camera);
  }
};

const resize = () => {
  if (!container.value || !renderer || !camera) return;

  const { clientWidth, clientHeight } = container.value;
  if (!clientWidth || !clientHeight) return;

  camera.aspect = clientWidth / clientHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(clientWidth, clientHeight, false);
  render();
};

const fitCameraToModel = () => {
  const box = new THREE.Box3().setFromObject(model);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const maxSize = Math.max(size.x, size.y, size.z);

  if (!Number.isFinite(maxSize) || maxSize === 0) return;

  const distance = (maxSize / (2 * Math.tan((camera.fov * Math.PI) / 360))) * 1.4;

  camera.near = Math.max(maxSize / 100, 0.01);
  camera.far = Math.max(maxSize * 100, 100);
  camera.position.copy(center).add(new THREE.Vector3(distance, distance * 0.75, distance));
  camera.updateProjectionMatrix();

  controls.target.copy(center);
  controls.minDistance = distance * 0.25;
  controls.maxDistance = distance * 4;
  controls.update();

  grid = new THREE.GridHelper(maxSize * 4, 12, 0x8c8c8c, 0xd9d9d9);
  grid.position.y = box.min.y;
  scene.add(grid);
};

const loadModel = () => {
  loadRequestId += 1;
  const requestId = loadRequestId;

  clearModel();
  errorMessage.value = '';
  loadProgress.value = null;

  if (!selectedModelUrl.value) {
    errorMessage.value = 'Для этой мебели не загружена 3D-модель';
    return;
  }

  isLoading.value = true;

  model3dLoader
    .load(selectedModelUrl.value, (progress) => {
      if (requestId !== loadRequestId || progress === null) return;
      loadProgress.value = progress;
    })
    .then((loadedScene) => {
      if (requestId !== loadRequestId) {
        disposeObject(loadedScene);
        return;
      }

      model = loadedScene;
      hasModel.value = true;
      scene.add(model);
      fitCameraToModel();
      loadProgress.value = 100;
      isLoading.value = false;
      render();
    })
    .catch((error) => {
      if (requestId !== loadRequestId) return;

      console.error(`Failed to load 3D model: ${selectedModelUrl.value}`, error);
      isLoading.value = false;
      errorMessage.value = 'Не удалось загрузить 3D-модель';
    });
};

const zoomCamera = (scale) => {
  if (!camera || !controls) return;

  const offset = camera.position.clone().sub(controls.target);
  const nextDistance = offset.length() * scale;
  const minDistance = controls.minDistance || 0.01;
  const maxDistance = controls.maxDistance || 1000;
  const clampedDistance = THREE.MathUtils.clamp(nextDistance, minDistance, maxDistance);

  offset.setLength(clampedDistance);
  camera.position.copy(controls.target).add(offset);
  controls.update();
  render();
};

const zoomIn = () => zoomCamera(0.82);
const zoomOut = () => zoomCamera(1.18);

const getDownloadFileName = () => {
  const safeName = String(props.modelName || 'furniture').replace(/[\\/:*?"<>|]+/g, '_');
  const qualitySuffix = qualityOptions.value.length ? `_${selectedQuality.value}` : '';
  return `${safeName}${qualitySuffix}.glb`;
};

const downloadModel = () => {
  if (!selectedModelUrl.value) return;

  const link = document.createElement('a');
  link.href = selectedModelUrl.value;
  link.download = getDownloadFileName();
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const toggleFullscreen = async () => {
  if (!viewer.value) return;

  if (document.fullscreenElement === viewer.value) {
    await document.exitFullscreen();
    return;
  }

  await viewer.value.requestFullscreen();
};

const handleFullscreenChange = () => {
  isFullscreen.value = document.fullscreenElement === viewer.value;
  requestAnimationFrame(resize);
};

const animate = () => {
  animationFrameId = window.requestAnimationFrame(animate);
  controls?.update();
  render();
};

const startAnimation = () => {
  if (animationFrameId !== null) return;
  animate();
};

const stopAnimation = () => {
  if (animationFrameId === null) return;
  window.cancelAnimationFrame(animationFrameId);
  animationFrameId = null;
};

onMounted(() => {
  scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf5f5f5);

  camera = new THREE.PerspectiveCamera(45, 1, 0.01, 1000);

  renderer = new THREE.WebGLRenderer({ antialias: true });
  model3dLoader.setSupportRenderer(renderer);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  container.value.appendChild(renderer.domElement);

  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;

  scene.add(new THREE.HemisphereLight(0xffffff, 0x666666, 2.5));

  const directionalLight = new THREE.DirectionalLight(0xffffff, 2.5);
  directionalLight.position.set(4, 7, 5);
  scene.add(directionalLight);

  resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(container.value);
  document.addEventListener('fullscreenchange', handleFullscreenChange);

  resize();
  loadModel();
  if (props.active) startAnimation();
});

watch(selectedModelUrl, () => {
  if (scene) {
    loadModel();
  }
});

watch(qualityOptions, (options) => {
  if (!options.length) return;

  if (options.some((option) => option.value === selectedQuality.value)) {
    return;
  }

  selectedQuality.value = fallbackVariant.value?.quality || options[0].value;
}, { immediate: true });

watch(normalizedDefaultQuality, (quality) => {
  if (qualityOptions.value.some((option) => option.value === quality)) {
    selectedQuality.value = quality;
  }
});

watch(() => props.active, (active) => {
  if (active) {
    resize();
    startAnimation();
  } else {
    stopAnimation();
  }
});

onBeforeUnmount(() => {
  loadRequestId += 1;
  resizeObserver?.disconnect();
  document.removeEventListener('fullscreenchange', handleFullscreenChange);

  stopAnimation();

  clearModel();
  controls?.dispose();
  renderer?.dispose();
});
</script>

<style scoped>
.furniture-3d-viewer {
  position: relative;
  width: 100%;
  height: min(65vh, 680px);
  min-height: 420px;
  overflow: hidden;
  background: #f5f5f5;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
}

.furniture-3d-viewer:fullscreen {
  width: 100vw;
  height: 100vh;
  min-height: 100vh;
  border: 0;
  border-radius: 0;
}

.furniture-3d-viewer__toolbar {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: rgb(255 255 255 / 88%);
  border: 1px solid rgb(195 210 228 / 55%);
  border-radius: 11px;
  box-shadow: 0 4px 24px rgb(28 42 66 / 13%);
  backdrop-filter: blur(14px);
}

.furniture-3d-viewer__tool-button {
  width: 36px;
  height: 36px;
  color: #405169;
  border-radius: 0;
}

.furniture-3d-viewer__tool-button:hover,
.furniture-3d-viewer__tool-button:focus {
  color: #2f6df6;
  background: rgb(47 109 246 / 8%);
}

.furniture-3d-viewer__tool-button[disabled] {
  color: #c5d0de;
}

.furniture-3d-viewer__divider {
  height: 1px;
  background: rgb(180 198 220 / 38%);
}

.furniture-3d-viewer__settings-panel {
  width: 178px;
  max-width: 238px;
  overflow: hidden;
  background: rgb(250 251 253 / 85%);
  border: 1px solid rgb(255 255 255 / 70%);
  border-radius: 14px;
  box-shadow:
    0 14px 40px rgb(20 33 54 / 20%),
    0 2px 8px rgb(20 33 54 / 10%);
  backdrop-filter: blur(20px) saturate(140%);
}

.furniture-3d-viewer__settings-section-label {
  padding: 13px 12px 4px;
  font-size: 9.5px;
  font-weight: 700;
  color: #8a98ab;
  text-transform: uppercase;
  letter-spacing: 0.09em;
}

.furniture-3d-viewer__settings-section {
  padding: 0 12px 12px;
}

.furniture-3d-viewer__quality {
  width: 100%;
}

.furniture-3d-viewer__canvas {
  width: 100%;
  height: 100%;
}

.furniture-3d-viewer__canvas :deep(canvas) {
  display: block;
  width: 100%;
  height: 100%;
  cursor: grab;
}

.furniture-3d-viewer__canvas :deep(canvas:active) {
  cursor: grabbing;
}

.furniture-3d-viewer__message {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #595959;
  background: rgb(245 245 245 / 85%);
}

.furniture-3d-viewer__progress {
  width: min(360px, 80%);
  height: 4px;
  overflow: hidden;
  background: #d9d9d9;
  border-radius: 2px;
}

.furniture-3d-viewer__progress-value {
  height: 100%;
  background: #1677ff;
  border-radius: inherit;
  transition: width 160ms ease;
}

.furniture-3d-viewer__progress-value--indeterminate {
  width: 45%;
  animation: furniture-3d-viewer-progress 1.1s ease-in-out infinite;
}

.furniture-3d-viewer__message--error {
  color: #cf1322;
}

@keyframes furniture-3d-viewer-progress {
  from {
    transform: translateX(-110%);
  }

  to {
    transform: translateX(260%);
  }
}

@media (max-width: 767px) {
  .furniture-3d-viewer {
    height: 420px;
    min-height: 320px;
  }

  .furniture-3d-viewer__toolbar {
    top: 8px;
    left: 8px;
  }
}

:global(.furniture-3d-viewer-settings-popover .ant-popover-inner) {
  padding: 0;
  overflow: hidden;
  background: transparent;
  border-radius: 14px;
  box-shadow: none;
}

:global(.furniture-3d-viewer-settings-popover .ant-popover-arrow) {
  display: none;
}
</style>
